/* eslint-disable no-console */
import {
  VERCEL_BLOB_PRELOAD_IDLE_URLS,
  VERCEL_BLOB_PRELOAD_LAZY_URLS,
  VERCEL_BLOB_PRELOAD_MANIFEST_FINGERPRINT,
} from './vercelBlobPreloadUrls'

/**
 * Per-destination asset manifests for the route loading screens.
 *
 * The cold gate covers the land page and everything one click from it. Everything deeper used to
 * arrive whenever it happened to arrive — background sweep if you were lucky, an on-demand fetch
 * under a live UI if you were not, which is what made those screens look half-built. Instead each
 * destination now owns a manifest and blocks on it behind the same loading screen the game boots
 * with: the player waits once, then the screen is complete.
 *
 * Lists are DERIVED from the existing manifest rather than hand-written, so artwork added to any
 * of these areas is picked up automatically. Gate URLs are excluded — they are already warm by the
 * time any of this runs.
 */

export type RouteAssetArea =
  | 'kingdom'
  | 'world-map'
  | 'pvp'
  | 'alliance'
  | 'pve'
  | 'mvm'
  | 'minigame'

/**
 * Matched against the lowercased URL pathname. Order matters: `AREA_ORDER` decides which area
 * claims a URL when more than one pattern would match, so narrower areas come first.
 */
const AREA_MATCHERS: Record<RouteAssetArea, readonly RegExp[]> = {
  // Palace, court, diplomacy, captives. Tech-research artwork is already gated.
  kingdom: [/^\/palace\//, /^\/_merged_\/kingdom\//, /^\/land\/diplomacy\//, /^\/_merged_\/training\//],
  // TerrainPacker atlases and overlays for the full world map.
  'world-map': [/^\/assets\/maps?\//, /^\/redesign\/world-map\//],
  // Hub, tournament, 1v1/2v2/3v3, victory and leave modals, battle sprites, leaderboards.
  pvp: [/^\/pvp\//, /^\/leaderboard\//],
  alliance: [/^\/land\/alliance\//, /^\/redesign\/alliance\//],
  // Chapter maps and the card-battle screen.
  pve: [/^\/pve\//, /^\/battle_action_cards\//, /^\/assets\/battle\//, /^\/idles\//],
  // Hex battle. The animation frames alone are ~1200 files, so this is deliberately its own area.
  mvm: [/^\/animations\/mvm-characters\//, /^\/mvm\//, /^\/assets\/mvm/],
  // Onet (`/eclipsera-bonds`).
  minigame: [/^\/redesign\/onet-assets\//, /^\/redesign\/mini-game\//, /^\/redesign\/mini-claim-modal\//],
}

/** Narrower areas first so a shared folder cannot be claimed by a broader one. */
const AREA_ORDER: readonly RouteAssetArea[] = [
  'minigame',
  'mvm',
  'world-map',
  'alliance',
  'pve',
  'pvp',
  'kingdom',
]

/** Video streams on demand; buffering it whole to satisfy a loading bar helps nobody. */
const ROUTE_ASSET_EXCLUDE = /\.(mp4|webm|mov|m4v)(\?|$)/i

function pathnameOf(url: string): string {
  try {
    return new URL(url).pathname.toLowerCase()
  } catch {
    return url.toLowerCase()
  }
}

function buildAreaManifests(): Record<RouteAssetArea, string[]> {
  const out = {
    kingdom: [],
    'world-map': [],
    pvp: [],
    alliance: [],
    pve: [],
    mvm: [],
    minigame: [],
  } as Record<RouteAssetArea, string[]>

  // Gate URLs are omitted: they are warm before any route gate can run.
  const candidates = [...VERCEL_BLOB_PRELOAD_IDLE_URLS, ...VERCEL_BLOB_PRELOAD_LAZY_URLS]
  for (const url of candidates) {
    if (ROUTE_ASSET_EXCLUDE.test(url)) continue
    const path = pathnameOf(url)
    for (const area of AREA_ORDER) {
      if (AREA_MATCHERS[area].some((re) => re.test(path))) {
        out[area].push(url)
        break
      }
    }
  }
  return out
}

const AREA_MANIFESTS = buildAreaManifests()

export function getRouteAssetUrls(area: RouteAssetArea): readonly string[] {
  return AREA_MANIFESTS[area]
}

/**
 * Warm-flag key for one area. Includes the global manifest fingerprint so that changing the asset
 * list re-gates every area rather than trusting a stale flag.
 */
export function routeAssetWarmKey(area: RouteAssetArea): string {
  return `${VERCEL_BLOB_PRELOAD_MANIFEST_FINGERPRINT}:route:${area}`
}

/** Assets not claimed by any area — logged under `?debugload=1` so gaps stay visible. */
export function unclaimedRouteAssets(): string[] {
  const claimed = new Set(Object.values(AREA_MANIFESTS).flat())
  return [...VERCEL_BLOB_PRELOAD_IDLE_URLS, ...VERCEL_BLOB_PRELOAD_LAZY_URLS].filter(
    (u) => !claimed.has(u) && !ROUTE_ASSET_EXCLUDE.test(u),
  )
}
